<template>
  <div
    class="fb-login-button social-login-btn"
    data-width=""
    data-max-rows="1"
    data-size="large"
    data-button-type="continue_with"
    data-auto-logout-link="false"
    data-use-continue-as="true"
    data-scope="public_profile,email"
    :data-onlogin="onLoginCallback"
  ></div>
</template>

<script lang="ts">
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'
import { AuthMachineInterpreter } from '../../apps/auth/authMachine'
import { getAuthService } from '../../apps/auth/service'
import { logger } from '../../utility/logger'

interface FacebookSDK {
  init(params: FacebookInitParams): void
  getLoginStatus(
    callback: (response: FacebookLoginStatusResponse) => void,
  ): void
  login(
    callback: (response: FacebookAuthResponse) => void,
    options?: FacebookLoginOptions,
  ): void
  api(path: string, callback: (response: any) => void): void
  Event: {
    subscribe(event: string, callback: (...args: any[]) => void): void
    unsubscribe(event: string, callback: (...args: any[]) => void): void
  }
  XFBML: {
    parse(element?: Element): void
  }
}

interface FacebookInitParams {
  appId: string
  cookie?: boolean
  xfbml?: boolean
  version: string
  status?: boolean
  frictionlessRequests?: boolean
}

interface FacebookAuthResponse {
  accessToken: string
  expiresIn: string
  signedRequest: string
  userID: string
}

interface FacebookLoginStatusResponse {
  status: 'connected' | 'not_authorized' | 'unknown'
  authResponse?: FacebookAuthResponse
}

interface FacebookLoginOptions {
  scope?: string
  return_scopes?: boolean
  enable_profile_selector?: boolean
  profile_selector_ids?: string
  auth_type?: 'rerequest'
}

declare global {
  interface Window {
    FB: FacebookSDK
    fbAsyncInit: () => void
    [key: string]: any // For dynamic callback function names
  }
}

@Component
class FacebookContinueButton extends Vue {
  authMachine: AuthMachineInterpreter
  onLoginCallback: string

  async mounted() {
    this.authMachine = getAuthService()
    this.onLoginCallback = `handleFacebookLogin_${this.$.uid}`

    // Create global callback function for this component instance
    window[this.onLoginCallback] = this.handleLogin.bind(this)

    // Initialize Facebook SDK
    window.fbAsyncInit = () => {
      logger('fbAsyncInit - Continue Button')
      window.FB.init({
        appId: this.$config.public.facebookApiKey,
        cookie: true,
        xfbml: true,
        version: 'v23.0', // Updated to latest API version
      })
    }

    // Load Facebook SDK
    await this.$loadScriptWithPromise(
      'https://connect.facebook.net/en_US/sdk.js',
      'FBContinueLogin',
      { 'data-cookieyes': 'cookieyes-functional' },
    )
  }

  unmounted() {
    if (this && this.$unloadScript) {
      this.$unloadScript('FBContinueLogin')
    }
    // Clean up global callback
    if (
      this &&
      this.onLoginCallback &&
      window &&
      window[this.onLoginCallback]
    ) {
      delete window[this.onLoginCallback]
    }
  }

  handleLogin(response: any) {
    logger('Facebook login response:', response)

    if (response.status === 'connected' && response.authResponse?.accessToken) {
      // User successfully logged in - send token to social auth backend
      this.authByAccessToken(response.authResponse.accessToken)
    } else if (response.status === 'not_authorized') {
      // User is logged into Facebook but hasn't authorized the app
      logger('User is logged into Facebook but not authorized')
    } else {
      // User isn't logged into Facebook
      logger('User not logged into Facebook')
    }
  }

  private authByAccessToken(token: string) {
    // Extract next parameter from query, fallback to current page
    const next = (this.$route.query.next as string) || this.$route.fullPath

    // Send Facebook access token to social auth backend with next param
    this.authMachine.send('LOG_IN_FACEBOOK', {
      token,
      next,
    })
  }
}

export default toNative(FacebookContinueButton)
</script>

<style scoped>
/* Override Facebook button styles if needed */
:deep(.fb-login-button) {
  vertical-align: top;
}

:deep(.fb-login-button iframe) {
  vertical-align: top;
}
</style>
